//https://auth0.com/docs/quickstart/spa/react/02-calling-an-api#call-the-api
import axios from "axios";
import reactotron from "ReactotronConfig";
//import basic from 'basic-authorization-header';

import { sec } from "utils/security";

const CSV_CHECK_TIMEOUT_MS = 60000;
const CSV_VALIDATE_TIMEOUT_MS = 60000;
const VEHICLES_TIMEOUT_MS = 60000;
const TRANSPORT_TASKS_TIMEOUT_MS = 60000;

const backendAPI = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_API_ENDPOINT,
  // baseURL: 'http://localhost:3001',
  timeout: 30000,
});

function extractResponseMetadata(response) {
  return {
    data: response.data,
    etag: response.headers.etag || null,
    contractVersion: response.headers["x-transport-task-contract-version"] || null,
  };
}

//Interceptors
//Inject JWT Access Token to each request
backendAPI.interceptors.request.use(
  async (config) => {
    const token = await sec.getAccessTokenSilently()();
    config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);
//return data from each response and reject in case of error which will trigger ModalError
backendAPI.interceptors.response.use(
  (response) => {
    reactotron.logImportant('backendAPI', response.config.url, response)
    if (response.config.includeResponseMetadata) {
      return extractResponseMetadata(response);
    }
    return response.data;
  },
  function (error) {
    return Promise.reject(error);
  }
);

async function checkCSV(fileData) {
  let formData = new FormData();
  formData.append(fileData.fileName, fileData.content);
  let headers = {
    "Content-Type": "multipart/form-data",
  };
  return backendAPI.post("/csv/check", formData, {
    headers,
    timeout: CSV_CHECK_TIMEOUT_MS,
  });
}

async function validateCSV(fileName) {
  return backendAPI.get("/csv/validate/" + fileName, {
    timeout: CSV_VALIDATE_TIMEOUT_MS,
  });
}

async function getVehicles() {
  return backendAPI.get("/vehicles", {
    timeout: VEHICLES_TIMEOUT_MS,
  });
}

async function getCompanies() {
  return backendAPI.get("/manualcostcompanies");
}

async function getLocations() {
  return backendAPI.get("/manualcostlocations");
}

async function getCostTypes() {
  return backendAPI.get("/manualcosttypes");
}

async function addCost(costdata) {
  return backendAPI.post("/cost", costdata);
}

async function getUnlinkedRevenues() {
  return backendAPI.get("/unlinkedrevenues");
}

async function linkVehicleToRevenue(linkdata) {
  return backendAPI.post(
    `/vehicle/${linkdata.vehicle_id}/link`,
    linkdata.revenue_id
  );
}

async function getRevenueTypes() {
  return backendAPI.get("/manualrevenuetypes");
}

async function addRevenue(revenuedata) {
  return backendAPI.post("/revenue", revenuedata);
}

async function getLatestCosts() {
  return backendAPI.get("/latestmanualcosts");
}

async function deleteCost(id) {
  return backendAPI.delete("/cost/" + id);
}

async function getLatestRevenues() {
  return backendAPI.get("/latestmanualrevenues");
}

async function deleteRevenue(id) {
  return backendAPI.delete("/revenue/" + id);
}

async function getCostGroups() {
  return backendAPI.get("/costgroups");
}

async function getRevenueGroups() {
  return backendAPI.get("/revenuegroups");
}

async function getUngroupedCostTypes() {
  return backendAPI.get("/costtypes/ungrouped");
}
async function getUngroupedRevenueTypes() {
  return backendAPI.get("/revenuetypes/ungrouped");
}

async function linkCosttypesToGroup(linkdata) {
  return backendAPI.post(
    `/costtypes/${linkdata.group_id}/link`,
    linkdata.costtypes
  );
}

async function linkRevenuetypesToGroup(linkdata) {
  return backendAPI.post(
    `/revenuetypes/${linkdata.group_id}/link`,
    linkdata.revenuetypes
  );
}

// Service fleet APIs
async function getVehicleServiceData() {
  return backendAPI.get("/servicevehicles");
}

async function updateVehicleServiceData(servicedata) {
  return backendAPI.put("/servicevehicles", servicedata);
}

async function getVehicleServiceHistory(vin) {
  return backendAPI.get(`/serviceevent/${vin}`);
}

async function getVehicleServiceAxels(id) {
  return backendAPI.get(`/serviceaxels/${id}`);
}

async function updateVehicleService(id, data) {
  return backendAPI.put(`/servicevehicles/${id}`, data)
}

async function addService(id, data) {
  return backendAPI.post(`/serviceevent/${id}`, data)
}

async function getViewServices(id) {
  return backendAPI.get(`/serviceevent/${id}`)
}

async function deleteViewServiceById(id) {
  return backendAPI.delete(`/serviceevent/${id}`)
}

// Transport task APIs are intentionally read-only in the first TMS release.
// The detail response retains the server ETag so the UI can expose the exact
// authoritative version without creating a client-side business state.
async function getTransportTasks() {
  return backendAPI.get("/ai/transport-tasks", {
    params: {
      status: "planned,active,blocked",
      limit: 100,
    },
    includeResponseMetadata: true,
    timeout: TRANSPORT_TASKS_TIMEOUT_MS,
  });
}

async function getTransportTask(id) {
  return backendAPI.get(`/ai/transport-tasks/${encodeURIComponent(id)}`, {
    includeResponseMetadata: true,
    timeout: TRANSPORT_TASKS_TIMEOUT_MS,
  });
}

export {
  extractResponseMetadata,
  checkCSV,
  validateCSV,
  getVehicles,
  getCompanies,
  getLocations,
  getCostTypes,
  addCost,
  getUnlinkedRevenues,
  linkVehicleToRevenue,
  getRevenueTypes,
  addRevenue,
  getLatestCosts,
  getLatestRevenues,
  deleteCost,
  deleteRevenue,
  getCostGroups,
  getRevenueGroups,
  getUngroupedCostTypes,
  getUngroupedRevenueTypes,
  linkCosttypesToGroup,
  linkRevenuetypesToGroup,
  getVehicleServiceData,
  updateVehicleServiceData,
  getVehicleServiceHistory,
  getVehicleServiceAxels,
  updateVehicleService, addService,
  getViewServices,
  deleteViewServiceById,
  getTransportTasks,
  getTransportTask
};
